import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Clock, TrendingUp, Users, CheckCircle, XCircle } from 'lucide-react';
import { taskService } from '@/core/api';
import { LoadingSpinner, EmptyState } from '@/components';
import type { Event } from '@/types';

function TaskDetailPage() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();

  const { data: task, isLoading: taskLoading } = useQuery({
    queryKey: ['task', taskId],
    queryFn: () => taskService.getTask(taskId!),
    enabled: !!taskId,
  });

  const { data: events, isLoading: eventsLoading } = useQuery({
    queryKey: ['task-events', taskId],
    queryFn: () => taskService.getTaskEvents(taskId!),
    enabled: !!taskId,
  });

  if (taskLoading || eventsLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!task) {
    return (
      <EmptyState
        title="Task not found"
        description="The task you're looking for doesn't exist"
      />
    );
  }

  return (
    <div className="min-h-screen bg-telegram-bg pb-20">
      <div
        className="pointer-events-none fixed left-1/2 z-30 -translate-x-1/2"
        style={{ top: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
      >
        <div className="pointer-events-auto flex items-center gap-2 rounded-full bg-[var(--ff-surface)]/90 px-2 py-1 shadow-[0_12px_28px_rgba(0,0,0,0.08)] backdrop-blur">
          <button
            onClick={() => navigate(-1)}
            className="rounded-full border border-[var(--ff-border)] bg-[var(--ff-surface-alt)] px-3 py-2 text-sm font-semibold text-telegram-text shadow-sm hover:shadow transition"
          >
            <ArrowLeft size={18} />
          </button>
        </div>
      </div>

      {/* Task Info Card */}
      <div className="p-4 pt-20">
        <div className="card">
          <h2 className="text-xl font-bold text-telegram-text mb-2">
            {task.title || task.entity?.name || 'Untitled Task'}
          </h2>

          {task.description && (
            <p className="text-sm text-telegram-hint mb-4">{task.description}</p>
          )}

          {/* Status Badges */}
          <div className="flex items-center gap-2 mb-4">
            <span
              className={`px-3 py-1 text-xs font-semibold rounded-full ${
                task.status === 'active'
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
              }`}
            >
              {task.status.toUpperCase()}
            </span>

            {task.is_public && (
              <span className="px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 flex items-center gap-1">
                <Users size={12} />
                Public ({task.subscribers_count})
              </span>
            )}
          </div>

          <div className="border-t border-telegram-secondary pt-4 mt-4">
            {/* Metadata */}
            <div className="space-y-2 text-sm">
              <MetadataRow
                icon={<TrendingUp size={14} />}
                label="Type"
                value={task.intent_type.replace(/_/g, ' ').toUpperCase()}
              />
              <MetadataRow
                icon={<Clock size={14} />}
                label="Frequency"
                value={task.frequency}
              />
              <MetadataRow
                icon={<CheckCircle size={14} />}
                label="Channels"
                value={task.channels.join(', ')}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Timeline Section */}
      <div className="px-4 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Clock size={18} className="text-telegram-text" />
          <h3 className="text-lg font-bold text-telegram-text">Event Timeline</h3>
        </div>

        {/* Events List */}
        {!events || events.length === 0 ? (
          <div className="card text-center py-8">
            <div className="flex flex-col items-center gap-3">
              <XCircle size={48} className="text-telegram-hint opacity-50" />
              <div>
                <p className="text-telegram-text font-medium mb-1">No events yet</p>
                <p className="text-sm text-telegram-hint">
                  Events will appear here when the task is triggered
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {events.map((event, index) => (
              <TimelineEvent
                key={event.id}
                event={event}
                isLast={index === events.length - 1}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface MetadataRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function MetadataRow({ icon, label, value }: MetadataRowProps) {
  return (
    <div className="flex items-center gap-2 text-telegram-hint">
      {icon}
      <span className="text-telegram-hint">{label}:</span>
      <span className="text-telegram-text font-medium">{value}</span>
    </div>
  );
}

interface TimelineEventProps {
  event: Event;
  isLast: boolean;
}

function TimelineEvent({ event, isLast }: TimelineEventProps) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  return (
    <div className="flex gap-3">
      {/* Timeline Indicator */}
      <div className="flex flex-col items-center">
        <div className="w-3 h-3 bg-telegram-button rounded-full border-2 border-telegram-bg" />
        {!isLast && <div className="w-0.5 flex-1 bg-telegram-secondary min-h-[80px]" />}
      </div>

      {/* Event Card */}
      <div className="flex-1 pb-4">
        <div className="card">
          {/* Timestamp */}
          <p className="text-xs text-telegram-hint mb-2">
            {formatDate(event.triggered_at)}
          </p>

          {/* Event Payload */}
          <EventPayload payload={event.payload} />

          {/* Notified Channels */}
          {event.notified_channels.length > 0 && (
            <div className="flex gap-2 mt-3">
              {event.notified_channels.map((channel) => (
                <span
                  key={channel}
                  className="px-2 py-1 text-xs bg-telegram-secondary text-telegram-hint rounded flex items-center gap-1"
                >
                  <ChannelIcon channel={channel} />
                  {channel}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface EventPayloadProps {
  payload: Record<string, unknown>;
}

function EventPayload({ payload }: EventPayloadProps) {
  // Price event
  if ('price' in payload) {
    return (
      <div>
        <h4 className="font-semibold text-telegram-text mb-1">
          {payload.symbol as string} Price Alert
        </h4>
        <p className="text-sm text-telegram-hint">
          Price: {payload.currency as string}
          {payload.price as number}
        </p>
      </div>
    );
  }

  // News event
  if ('title' in payload && 'link' in payload) {
    return (
      <div>
        <h4 className="font-semibold text-telegram-text mb-1 line-clamp-2">
          {payload.title as string}
        </h4>
        <>
          {payload.summary && typeof payload.summary === 'string' && (
            <p className="text-sm text-telegram-hint line-clamp-3">{payload.summary}</p>
          )}
        </>
      </div>
    );
  }

  // Webpage change event
  if ('diff_summary' in payload) {
    return (
      <div>
        <h4 className="font-semibold text-telegram-text mb-1">Webpage Changed</h4>
        <p className="text-sm text-telegram-hint">
          {payload.diff_summary as string}
        </p>
        <>
          {payload.changes_count && typeof payload.changes_count === 'number' && (
            <p className="text-xs text-telegram-hint mt-1">
              {payload.changes_count} changes detected
            </p>
          )}
        </>
      </div>
    );
  }

  // Generic event
  return (
    <div>
      <p className="text-sm text-telegram-hint">
        {JSON.stringify(payload, null, 2)}
      </p>
    </div>
  );
}

function ChannelIcon({ channel }: { channel: string }) {
  switch (channel) {
    case 'app':
      return <CheckCircle size={12} />;
    case 'telegram':
      return <Users size={12} />;
    case 'email':
      return <Clock size={12} />;
    default:
      return null;
  }
}

export default TaskDetailPage;
